import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { AppThemeProvider } from '@pacxa/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.js';
import { setMsalInstance } from './api/client.js';
import { msalConfig } from './auth/msal.js';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry authorization failures — the answer will not change.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

async function bootstrap(): Promise<void> {
  const msal = new PublicClientApplication(msalConfig());
  await msal.initialize();

  const accounts = msal.getAllAccounts();
  if (accounts.length > 0 && !msal.getActiveAccount()) {
    msal.setActiveAccount(accounts[0]!);
  }

  msal.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account?: Parameters<typeof msal.setActiveAccount>[0] };
      if (payload.account) msal.setActiveAccount(payload.account);
    }
  });

  await msal.handleRedirectPromise();
  setMsalInstance(msal);

  const container = document.getElementById('root');
  if (!container) throw new Error('#root not found');

  createRoot(container).render(
    <StrictMode>
      {/* Outermost so that everything — including the signed-out and error
          states — is themed and follows the OS light/dark setting. */}
      <AppThemeProvider>
        <MsalProvider instance={msal}>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </MsalProvider>
      </AppThemeProvider>
    </StrictMode>,
  );
}

void bootstrap().catch((err: unknown) => {
  // Show what actually went wrong. A bare "failed to start" tells the user
  // nothing and tells whoever they contact even less — this is the only place
  // a startup failure surfaces, because React never mounts.
  const detail = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  console.error('[boot] failed', err);

  const container = document.getElementById('root');
  if (!container) return;
  container.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'font-family:"Segoe UI",system-ui,sans-serif;max-width:44rem;margin:4rem auto;padding:0 1.5rem;line-height:1.5';

  const h = document.createElement('h1');
  h.style.cssText = 'font-size:1.25rem;margin:0 0 .5rem';
  h.textContent = 'The application failed to start';

  const p1 = document.createElement('p');
  p1.style.cssText = 'margin:0 0 1rem;color:#444';
  p1.textContent =
    'Reload the page. If it keeps happening, send IT the detail below — it names the cause.';

  const pre = document.createElement('pre');
  pre.style.cssText =
    'background:#f3f2f1;border:1px solid #e1dfdd;border-radius:4px;padding:.75rem;' +
    'white-space:pre-wrap;word-break:break-word;font-size:.8125rem;margin:0';
  pre.textContent = detail;

  wrap.append(h, p1, pre);
  container.append(wrap);
});
